import { css } from '@emotion/react';
import { upgradeConditionMet } from '@mythos/game/CardAbilities';
import { CardAffinity, CardType } from '@mythos/game/CardTypes';
import { CardDef } from '@mythos/game/GameModel';
import { GamePlayerViewModel, GameViewModel } from '@mythos/game/GameViewModel';
import { Resource } from '@mythos/game/Resources';
import * as Rules from '@mythos/game/Rules';
import { makeContextFromGame } from '@mythos/game/Rules';
import { addToMapOfArrays } from '@mythos/utils/utils';
import nullthrows from 'nullthrows';
import React, { ReactElement, useCallback } from 'react';
import {
  BasicCardRenderer,
  CardRenderer,
  computeCardEffectRows,
} from './CardView';
import { UIColorPalette } from './ColorPalette';
import { EmptyCardView } from './EmptyCardView';
import { UserWithColor } from './GraphQLHelpers';
import HoverCardStore from './HoverCardStore';
import { MEDIA_QUERY_MOBILE } from './mobile_ui/MStyles';
import { BaseResourceToSymbol, ResourceToSymbol, SymbolView } from './Symbols';

const CARD_TYPE_TO_STACK_ORDER: Record<CardAffinity, number> = {
  [CardAffinity.Leader]: 0,
  [CardAffinity.Basic]: 1,
  [CardAffinity.None]: 2,
  [CardAffinity.Derion]: 3,
  [CardAffinity.Tyros]: 4,
  [CardAffinity.Zeona]: 5,
};

export type BoardViewModel = {
  stacks: Map<string, Rules.CardWithID[]>;
};
export function computeBoardViewModel(
  player: GamePlayerViewModel,
  game: GameViewModel,
): BoardViewModel {
  const stacks = new Map<string, Rules.CardWithID[]>();
  player.cards.forEach((card) => {
    addToMapOfArrays(stacks, card.type, card);
  });

  stacks.forEach((stack) => {
    stack.sort(
      (a, b) =>
        CARD_TYPE_TO_STACK_ORDER[a.affinity] -
        CARD_TYPE_TO_STACK_ORDER[b.affinity],
    );
  });

  return {
    stacks,
  };
}

type PlayerBoardViewProps = {
  player: GamePlayerViewModel;
  game: GameViewModel;
  user: UserWithColor;
  className?: string;
  style?: React.CSSProperties;
};
const PlayerBoardView = React.forwardRef<HTMLDivElement, PlayerBoardViewProps>(
  (props, ref) => {
    let { player, game, user } = props;

    const stacks = computeBoardViewModel(player, game).stacks;

    return (
      <div
        css={PlayerBoardStyles.container}
        className={props.className}
        ref={ref}
      >
        <div css={PlayerBoardStyles.header}>
          <div
            css={PlayerBoardStyles.name}
            style={{
              color: user.color,
            }}
          >
            {user.name}
          </div>
        </div>
        <StacksView
          className={props.className}
          stacks={[
            stacks.get(CardType.Unit) || [],
            stacks.get(CardType.Building) || [],
            stacks.get(CardType.Event) || [],
          ]}
          player={player}
          game={game}
          renderer={BasicCardRenderer}
          hoverRenderer={BasicCardRenderer}
        />
      </div>
    );
  },
);
export default PlayerBoardView;

export function PlayerCounter(props: {
  name: Resource;
  count: number;
  baseCount?: number;
  maxCount?: number;
  showMax?: boolean;
}) {
  const { name, count, baseCount, maxCount, showMax } = props;
  if (name === 'favor') {
    return null;
  }
  const isMaxed = maxCount !== undefined && count >= maxCount;
  const isNearMax = maxCount !== undefined && count === maxCount - 1;
  return (
    <div key={name} css={PlayerCounterStyles.counterContainer}>
      <SymbolView
        symbol={ResourceToSymbol[name as Resource]}
        css={PlayerCounterStyles.resourceSymbol}
      />
      <span
        css={[
          isMaxed && PlayerCounterStyles.maxedText,
          isNearMax && PlayerCounterStyles.nearMaxText,
        ]}
      >
        {count}
      </span>
      {showMax && maxCount !== undefined && (
        <span css={PlayerCounterStyles.maxText}>/{maxCount}</span>
      )}
      {baseCount !== undefined && (
        <>
          <SymbolView
            symbol={nullthrows(BaseResourceToSymbol[name])}
            css={PlayerCounterStyles.baseSymbol}
          />
          {baseCount}
        </>
      )}
    </div>
  );
}

const PlayerCounterStyles = {
  counterContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 10,
    fontSize: 16,
  }),
  resourceSymbol: css({
    height: 20,
    width: 20,
    marginRight: 4,
  }),
  baseSymbol: css({
    height: 15,
    width: 15,
    marginLeft: 4,
    marginRight: 2,
  }),
  maxedText: css({
    color: UIColorPalette.playerSummaryResourceTextMaxed,
  }),
  nearMaxText: css({
    color: UIColorPalette.playerSummaryResourceTextNearMaxed,
  }),
  maxText: css({
    fontSize: '0.65em',
    alignSelf: 'flex-end',
  }),
};

function StacksView(props: {
  stacks: Rules.CardWithID[][];
  renderer: CardRenderer;
  hoverRenderer: CardRenderer;
  player: GamePlayerViewModel;
  game: GameViewModel;
  className?: string;
  style?: React.CSSProperties;
}): ReactElement {
  const onCardEnter = useCallback((card: CardDef) => {
    HoverCardStore.setCard(card, props.hoverRenderer);
    HoverCardStore.setPosition('bottom-right');
  }, []);
  const onCardLeave = useCallback(() => {
    HoverCardStore.setCard(null, null);
  }, []);
  const context = makeContextFromGame(props.player, props.game);

  return (
    <div
      css={PlayerBoardStyles.stacksContainer}
      className={props.className}
      style={{
        ...props.style,
      }}
    >
      {props.stacks.map((stack, i) => {
        return (
          <div key={i} css={PlayerBoardStyles.stackContainer}>
            {stack.length === 0 && <EmptyCardView />}
            {stack.map((c, j) => {
              let marginTop = 0;
              if (j !== stack.length - 1) {
                marginTop = -130;
                const height = getStackHeight(c, context);
                marginTop += height;
              }

              return (
                <div
                  key={c.id}
                  css={PlayerBoardStyles.cardContainer}
                  style={{ marginTop: marginTop }}
                >
                  {props.renderer({
                    card: c,
                    onCardEnter,
                    onCardLeave,
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function getStackHeight(card: CardDef, context: Rules.Context): number {
  const { upgradeCondition } = computeCardEffectRows(card);

  if (upgradeCondition && upgradeConditionMet(upgradeCondition, context)) {
    return 32;
  }

  return 20;
}

const PlayerBoardStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  }),

  stacksContainer: css({
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: UIColorPalette.playerBoardBG,
    flexGrow: 1,
    [MEDIA_QUERY_MOBILE]: {
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
  }),
  stackContainer: css({
    display: 'flex',
    flexDirection: 'column-reverse',
    justifyContent: 'flex-end',
    padding: 4,
  }),
  cardContainer: css({}),

  header: css({
    label: 'player-board-header',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',

    height: 20,
    padding: 2,
    paddingLeft: 8,

    backgroundColor: UIColorPalette.playerBoardHeaderBG,
    color: UIColorPalette.playerNameText,

    [MEDIA_QUERY_MOBILE]: {
      display: 'none',
    },
  }),
  name: css({}),
};
